import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import {
  DELIVERY_OUTCOME_LABELS,
  DELIVERY_MODE_LABELS,
  EDD_BASIS_LABELS,
  FETAL_MOVEMENT_LABELS,
  formatGestation,
  formatObstetricHistory,
  LABOUR_STATUS_LABELS,
  PREGNANCY_CLOSE_OUTCOMES,
  PREGNANCY_OUTCOME_LABELS,
  PREGNANCY_SEX_ALERT_RULE,
  PREGNANCY_STATUS_LABELS,
  type EddBasis,
  type PregnancyCloseOutcome,
} from '@wodi/domain';
import type { MaternityView, PregnancyRecordView, PregnancyView } from '@wodi/shared';
import {
  AlertPanel,
  Button,
  Cell,
  Column,
  DateField,
  Dialog,
  DialogActions,
  LoadingBlock,
  Notice,
  PageHeader,
  RadioGroup,
  Row,
  Section,
  Select,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextArea,
  TextField,
} from '@wodi/ui';
import { useRef, useState } from 'react';

import {
  closePregnancy,
  correctPregnancy,
  maternityKeys,
  maternityQuery,
  openLabour,
  openPregnancy,
  pregnancyInError,
} from '../api/maternity.ts';
import { ApiError } from '../api/client.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { formatInstant, formatIsoDate } from '../patients/format.ts';
import { PatientBannerLoader } from '../patients/patient-banner.tsx';
import { REASON_MIN_LENGTH } from '../patients/reason-dialog.tsx';
import { toasts } from '../toasts.ts';
import { DangerSignWords, errorText, InErrorDialog, readWhole, setValues } from './common.tsx';
import styles from './maternity.module.css';

// A patient's maternity record (docs/plans/phase2-maternity-mch.md §6): the open pregnancy
// with its dating as entered, ANC contacts, labour and delivery, and earlier pregnancies.
// Gestation is weeks and days, never a category (plan §0).

const route = getRouteApi('/_app/patients/$patientId/maternity');

const NO_BASIS = 'none';

interface DatingState {
  lmp: string | null;
  edd: string | null;
  basis: string;
  gravida: string;
  parity: string;
  parityPlus: string;
}

function datingFrom(p: PregnancyView | null): DatingState {
  return {
    lmp: p?.lmp ?? null,
    edd: p?.eddBasis === 'entered' ? p.edd : null,
    basis: p?.eddBasis === 'entered' ? 'entered' : NO_BASIS,
    gravida: p?.gravida === null || p === null ? '' : String(p.gravida),
    parity: p?.parity === null || p === null ? '' : String(p.parity),
    parityPlus: p?.parityPlus === null || p === null ? '' : String(p.parityPlus),
  };
}

/** The dating as the API takes it; an EDD left empty is calculated from the LMP. */
function datingBody(d: DatingState) {
  const n = (t: string) => {
    const v = readWhole(t);
    return v === 'invalid' ? null : v;
  };
  const entered = d.edd !== null;
  return {
    lmp: d.lmp,
    edd: entered ? d.edd : null,
    eddBasis: entered ? ('entered' as EddBasis) : null,
    gravida: n(d.gravida),
    parity: n(d.parity),
    parityPlus: n(d.parityPlus),
  };
}

function datingInvalid(d: DatingState): boolean {
  return [d.gravida, d.parity, d.parityPlus].some((t) => readWhole(t) === 'invalid');
}

function DatingFields({
  value,
  onChange,
}: {
  value: DatingState;
  onChange: (d: DatingState) => void;
}) {
  const whole = (text: string) =>
    readWhole(text) === 'invalid' ? { errorMessage: 'A whole number.' } : {};
  return (
    <>
      <div className={styles.when}>
        <DateField
          label="First day of the last period (LMP)"
          value={value.lmp}
          onChange={(lmp) => {
            onChange({ ...value, lmp });
          }}
          description="Leave empty if not known."
        />
        <DateField
          label="EDD from elsewhere (for example a scan)"
          value={value.edd}
          onChange={(edd) => {
            onChange({ ...value, edd, basis: edd === null ? NO_BASIS : 'entered' });
          }}
          description="Leave empty to calculate it from the LMP: LMP + 280 days."
        />
      </div>
      <div className={styles.when}>
        <TextField
          label="Gravida"
          width="xs"
          inputMode="numeric"
          value={value.gravida}
          onChange={(gravida) => {
            onChange({ ...value, gravida });
          }}
          {...whole(value.gravida)}
        />
        <TextField
          label="Parity"
          width="xs"
          inputMode="numeric"
          value={value.parity}
          onChange={(parity) => {
            onChange({ ...value, parity });
          }}
          {...whole(value.parity)}
        />
        <TextField
          label="Parity, after the plus"
          width="xs"
          inputMode="numeric"
          value={value.parityPlus}
          onChange={(parityPlus) => {
            onChange({ ...value, parityPlus });
          }}
          description="As written on the card, e.g. P1+1."
          {...whole(value.parityPlus)}
        />
      </div>
    </>
  );
}

function OpenPregnancyForm({ view }: { view: MaternityView }) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const [visitId, setVisitId] = useState(view.openVisits[0]?.id ?? '');
  const [dating, setDating] = useState<DatingState>(datingFrom(null));
  // The interruptive alert for a patient not recorded as female (M25): asked by the server,
  // answered with a reason that is logged with the rule ID.
  const [sexAlert, setSexAlert] = useState(false);
  const [sexReason, setSexReason] = useState('');
  const [tried, setTried] = useState(false);
  const requestId = useRef(crypto.randomUUID());
  const reasonShort = sexAlert && sexReason.trim().length < REASON_MIN_LENGTH;
  const save = useMutation({
    mutationFn: () =>
      openPregnancy(visitId, {
        clientRequestId: requestId.current,
        ...datingBody(dating),
        acknowledgements: sexAlert
          ? [{ ruleId: PREGNANCY_SEX_ALERT_RULE, reason: sexReason.trim() }]
          : [],
      }),
    onSuccess: (next) => {
      queryClient.setQueryData(maternityKeys.patient(view.patientId), next);
      void queryClient.invalidateQueries({ queryKey: ['patients'] });
      requestId.current = crypto.randomUUID();
      setSexAlert(false);
      setSexReason('');
      setTried(false);
      toasts.show({ title: 'Pregnancy opened' });
    },
    onError: (error) => {
      requestId.current = crypto.randomUUID();
      if (
        error instanceof ApiError &&
        error.code === 'alert_acknowledgement_required' &&
        Array.isArray(error.context?.['ruleIds']) &&
        error.context['ruleIds'].includes(PREGNANCY_SEX_ALERT_RULE)
      ) {
        setSexAlert(true);
        setTried(false);
      }
    },
  });
  const asking =
    save.error instanceof ApiError && save.error.code === 'alert_acknowledgement_required';
  if (view.openVisits.length === 0) {
    return (
      <p className={styles.meta}>
        To open a pregnancy, check the patient in first: it is opened on one of her open visits.
      </p>
    );
  }
  return (
    <form
      className={styles.form}
      aria-label="Open pregnancy"
      onSubmit={(event) => {
        event.preventDefault();
        setTried(true);
        if (!datingInvalid(dating) && visitId !== '' && !reasonShort) save.mutate();
      }}
    >
      <Select
        label="Visit"
        width="md"
        options={view.openVisits.map((v) => ({
          id: v.id,
          label: `${v.visitNumber}, ${formatInstant(v.startedAt)}`,
        }))}
        value={visitId}
        onChange={(v) => {
          setVisitId(String(v));
        }}
      />
      <DatingFields value={dating} onChange={setDating} />
      {sexAlert ? (
        <AlertPanel
          title="This patient is not recorded as female"
          reason={sexReason}
          onReasonChange={setSexReason}
          {...(tried && reasonShort
            ? { reasonError: `Give a reason of at least ${String(REASON_MIN_LENGTH)} characters.` }
            : {})}
        >
          Check the recorded sex on the patient's record. To open the pregnancy anyway, say why.
        </AlertPanel>
      ) : null}
      {save.error === null || asking ? null : (
        <Notice tone="error">
          {errorText(save.error, 'The pregnancy was not opened. Try again.')}
        </Notice>
      )}
      <div className={styles.actions}>
        <Button
          type="submit"
          variant="primary"
          isDisabled={!online || datingInvalid(dating)}
          isPending={save.isPending}
          pendingLabel="Opening…"
        >
          Open pregnancy
        </Button>
      </div>
    </form>
  );
}

function CorrectDialog({
  pregnancy,
  patientId,
  onClose,
}: {
  pregnancy: PregnancyView;
  patientId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [dating, setDating] = useState<DatingState>(datingFrom(pregnancy));
  const [reason, setReason] = useState('');
  const save = useMutation({
    mutationFn: () =>
      correctPregnancy(pregnancy.id, {
        ...datingBody(dating),
        reason: reason.trim(),
        version: pregnancy.version,
      }),
    onSuccess: (next) => {
      queryClient.setQueryData(maternityKeys.patient(patientId), next);
      void queryClient.invalidateQueries({ queryKey: ['patients'] });
      toasts.show({ title: 'Pregnancy corrected' });
      onClose();
    },
  });
  return (
    <Dialog
      title="Correct the pregnancy"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <p className={styles.meta}>The earlier dating is kept as a version, with your reason.</p>
      <DatingFields value={dating} onChange={setDating} />
      <TextArea label="Why the correction?" value={reason} onChange={setReason} rows={2} />
      {save.error === null ? null : (
        <Notice tone="error">{errorText(save.error, 'Nothing was changed. Try again.')}</Notice>
      )}
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isDisabled={datingInvalid(dating) || reason.trim().length < REASON_MIN_LENGTH}
          isPending={save.isPending}
          pendingLabel="Saving…"
          onPress={() => {
            save.mutate();
          }}
        >
          Save correction
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CloseDialog({
  pregnancy,
  patientId,
  onClose,
}: {
  pregnancy: PregnancyView;
  patientId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [outcome, setOutcome] = useState<PregnancyCloseOutcome>('pregnancy_loss');
  const [note, setNote] = useState('');
  const save = useMutation({
    mutationFn: () =>
      closePregnancy(pregnancy.id, { outcome, note: note.trim() === '' ? null : note.trim() }),
    onSuccess: (next) => {
      queryClient.setQueryData(maternityKeys.patient(patientId), next);
      void queryClient.invalidateQueries({ queryKey: ['patients'] });
      toasts.show({ title: 'Pregnancy closed' });
      onClose();
    },
  });
  return (
    <Dialog
      title="Close the pregnancy"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <p className={styles.meta}>A delivery closes the pregnancy by itself; use this otherwise.</p>
      <RadioGroup
        label="Outcome"
        value={outcome}
        onChange={(v) => {
          setOutcome(v as PregnancyCloseOutcome);
        }}
        options={PREGNANCY_CLOSE_OUTCOMES.map((o) => ({
          id: o,
          label: PREGNANCY_OUTCOME_LABELS[o],
        }))}
      />
      <TextArea label="Note" value={note} onChange={setNote} rows={2} />
      {save.error === null ? null : (
        <Notice tone="error">{errorText(save.error, 'Nothing was changed. Try again.')}</Notice>
      )}
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isPending={save.isPending}
          pendingLabel="Saving…"
          onPress={() => {
            save.mutate();
          }}
        >
          Close pregnancy
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function OpenLabourButton({ view, pregnancy }: { view: MaternityView; pregnancy: PregnancyView }) {
  const navigate = useNavigate();
  const online = useOnline();
  const requestId = useRef(crypto.randomUUID());
  const admission = view.admission;
  const open = useMutation({
    mutationFn: () =>
      openLabour(admission?.id ?? '', {
        clientRequestId: requestId.current,
        pregnancyId: pregnancy.id,
        labourOnsetAt: null,
        membranesRupturedAt: null,
      }),
    onSuccess: (opened) => {
      toasts.show({ title: 'Labour record opened' });
      void navigate({
        to: '/labour/$labourRecordId',
        params: { labourRecordId: opened.labourRecordId },
      });
    },
    onError: () => {
      requestId.current = crypto.randomUUID();
    },
  });
  if (admission === null) {
    return (
      <p className={styles.meta}>
        A labour record is opened on a stay. Admit her first, to the labour ward or any ward.
      </p>
    );
  }
  return (
    <div className={styles.actions}>
      <Button
        isDisabled={!online}
        isPending={open.isPending}
        pendingLabel="Opening…"
        onPress={() => {
          open.mutate();
        }}
      >
        Open labour record
      </Button>
      <span className={styles.meta}>
        {admission.wardName === null
          ? 'On her current stay.'
          : `On her stay in ${admission.wardName}.`}
      </span>
      {open.error === null ? null : (
        <Notice tone="error">
          {errorText(open.error, 'The labour record was not opened. Try again.')}
        </Notice>
      )}
    </div>
  );
}

function PregnancyFacts({ p }: { p: PregnancyView }) {
  return (
    <dl className={styles.facts}>
      <dt>LMP</dt>
      <dd>{p.lmp === null ? 'Not known' : formatIsoDate(p.lmp)}</dd>
      <dt>EDD</dt>
      <dd>
        {p.edd === null
          ? 'Not known'
          : `${formatIsoDate(p.edd)} (${p.eddBasis === null ? '' : EDD_BASIS_LABELS[p.eddBasis].toLowerCase()})`}
      </dd>
      {p.status === 'open' ? (
        <>
          <dt>Gestation today</dt>
          <dd>
            {p.gestationToday === null
              ? 'Not known: no LMP or EDD'
              : formatGestation(p.gestationToday)}
          </dd>
        </>
      ) : null}
      <dt>Obstetric history</dt>
      <dd>{formatObstetricHistory(p)}</dd>
      <dt>Opened</dt>
      <dd>{`${formatInstant(p.openedAt)} at ${p.facilityName}, by ${p.openedBy.displayName}`}</dd>
      {p.outcome === null ? null : (
        <>
          <dt>Outcome</dt>
          <dd>{`${PREGNANCY_OUTCOME_LABELS[p.outcome]}${p.closedAt === null ? '' : `, ${formatInstant(p.closedAt)}`}`}</dd>
        </>
      )}
      {p.statusReason === null ? null : (
        <>
          <dt>Reason</dt>
          <dd>{p.statusReason}</dd>
        </>
      )}
      {p.versions.length > 1 ? (
        <>
          <dt>Corrections</dt>
          <dd>
            {p.versions
              .filter((v) => v.version > 1)
              .map(
                (v) =>
                  `Version ${String(v.version)}, ${formatInstant(v.changedAt)}, ${v.changedBy.displayName}: ${v.changeReason ?? ''}`,
              )
              .join('. ')}
          </dd>
        </>
      ) : null}
    </dl>
  );
}

function AncTable({ record }: { record: PregnancyRecordView }) {
  return (
    <Table flat dense aria-label="ANC contacts">
      <TableHeader>
        <Column isRowHeader>Contact</Column>
        <Column>When</Column>
        <Column>Gestation</Column>
        <Column>Findings</Column>
        <Column>Danger signs</Column>
        <Column>State</Column>
      </TableHeader>
      <TableBody items={record.ancContacts} renderEmptyState={() => 'No ANC contacts yet.'}>
        {(c) => (
          <Row id={c.id}>
            <Cell>
              <Link to="/visits/$visitId/anc" params={{ visitId: c.visitId }}>
                {`Contact ${String(c.contactNumber)}`}
              </Link>
            </Cell>
            <Cell>
              <span className={styles.numeric}>{formatInstant(c.contactAt)}</span>
            </Cell>
            <Cell>{c.gestation === null ? 'Not known' : formatGestation(c.gestation)}</Cell>
            <Cell>
              {[
                c.fundalHeightCm === null ? null : `Fundal height ${String(c.fundalHeightCm)} cm`,
                c.fetalHeartRate === null ? null : `FHR ${String(c.fetalHeartRate)}/min`,
                c.fetalMovement === null
                  ? null
                  : `Fetal movements: ${FETAL_MOVEMENT_LABELS[c.fetalMovement].toLowerCase()}`,
                c.vitalSet === null ? null : setValues(c.vitalSet),
              ]
                .filter((x) => x !== null)
                .join('; ') || 'None entered'}
            </Cell>
            <Cell>
              <DangerSignWords signs={c.dangerSigns} none={c.noDangerSigns} />
            </Cell>
            <Cell>
              {c.status === 'entered_in_error' ? (
                <Tag state="neutral">Entered in error</Tag>
              ) : (
                <Tag state="done">Recorded</Tag>
              )}
            </Cell>
          </Row>
        )}
      </TableBody>
    </Table>
  );
}

function LabourAndDelivery({ record }: { record: PregnancyRecordView }) {
  if (record.labours.length === 0 && record.deliveries.length === 0) {
    return <p className={styles.meta}>No labour record yet.</p>;
  }
  return (
    <ul className={styles.form} aria-label="Labour and delivery">
      {record.labours.map((l) => (
        <li key={l.id}>
          <Link to="/labour/$labourRecordId" params={{ labourRecordId: l.id }}>
            {`Labour record, opened ${formatInstant(l.openedAt)}`}
          </Link>{' '}
          <Tag state={l.status === 'in_labour' ? 'active' : 'done'}>
            {LABOUR_STATUS_LABELS[l.status]}
          </Tag>
        </li>
      ))}
      {record.deliveries.map((d) => (
        <li key={d.id} className={d.status === 'entered_in_error' ? styles.struck : undefined}>
          {`Delivered ${formatInstant(d.deliveredAt)}: ${DELIVERY_MODE_LABELS[d.mode]}; ${d.babies
            .map(
              (b) =>
                `baby ${String(b.birthOrder)} ${DELIVERY_OUTCOME_LABELS[b.outcome].toLowerCase()}`,
            )
            .join(', ')}`}
        </li>
      ))}
    </ul>
  );
}

function CurrentPregnancy({
  view,
  record,
  mayRecord,
}: {
  view: MaternityView;
  record: PregnancyRecordView;
  mayRecord: boolean;
}) {
  const queryClient = useQueryClient();
  const online = useOnline();
  const [dialog, setDialog] = useState<'correct' | 'close' | 'error' | null>(null);
  const p = record.pregnancy;
  const markError = useMutation({
    mutationFn: (reason: string) => pregnancyInError(p.id, reason),
    onSuccess: (next) => {
      queryClient.setQueryData(maternityKeys.patient(view.patientId), next);
      void queryClient.invalidateQueries({ queryKey: ['patients'] });
      toasts.show({ title: 'Pregnancy entered in error' });
      setDialog(null);
    },
  });
  return (
    <>
      <Section
        title="Pregnancy"
        actions={<Tag state="active">{PREGNANCY_STATUS_LABELS[p.status]}</Tag>}
      >
        <PregnancyFacts p={p} />
        {mayRecord ? (
          <div className={styles.actions}>
            <Button
              isDisabled={!online}
              onPress={() => {
                setDialog('correct');
              }}
            >
              Correct
            </Button>
            <Button
              isDisabled={!online}
              onPress={() => {
                setDialog('close');
              }}
            >
              Close
            </Button>
            <Button
              variant="quiet"
              isDisabled={!online}
              onPress={() => {
                setDialog('error');
              }}
            >
              Enter in error
            </Button>
          </div>
        ) : null}
      </Section>
      <Section title="ANC contacts">
        {mayRecord && view.openVisits.length > 0 ? (
          <nav className={styles.links} aria-label="Record an ANC contact">
            {view.openVisits.map((v) => (
              <Link key={v.id} to="/visits/$visitId/anc" params={{ visitId: v.id }}>
                {`Record ANC contact on visit ${v.visitNumber}`}
              </Link>
            ))}
          </nav>
        ) : null}
        <AncTable record={record} />
      </Section>
      <Section title="Labour and delivery">
        {mayRecord && record.labours.every((l) => l.status === 'entered_in_error') ? (
          <OpenLabourButton view={view} pregnancy={p} />
        ) : null}
        <LabourAndDelivery record={record} />
      </Section>
      {dialog === 'correct' ? (
        <CorrectDialog
          pregnancy={p}
          patientId={view.patientId}
          onClose={() => {
            setDialog(null);
          }}
        />
      ) : null}
      {dialog === 'close' ? (
        <CloseDialog
          pregnancy={p}
          patientId={view.patientId}
          onClose={() => {
            setDialog(null);
          }}
        />
      ) : null}
      {dialog === 'error' ? (
        <InErrorDialog
          what="this pregnancy"
          onClose={() => {
            setDialog(null);
          }}
          onConfirm={(reason) => {
            markError.mutate(reason);
          }}
          pending={markError.isPending}
          error={markError.error}
        />
      ) : null}
    </>
  );
}

export function MaternityPage() {
  const { patientId } = route.useParams();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const maternity = useQuery(maternityQuery(patientId));
  if (session === null || !can(session, 'maternity.read')) {
    return (
      <Notice tone="info">
        You don't have permission to see maternity records. Ask an administrator if you need it.
      </Notice>
    );
  }
  const mayRecord = can(session, 'maternity.record');
  return (
    <div className={styles.page}>
      <PatientBannerLoader patientId={patientId} />
      <PageHeader
        title="Maternity"
        actions={
          <nav className={styles.links} aria-label="Related">
            {can(session, 'mch.read') ? (
              <Link to="/patients/$patientId/child-health" params={{ patientId }}>
                Child health
              </Link>
            ) : null}
            <Link to="/patients/$patientId" params={{ patientId }}>
              Patient record
            </Link>
          </nav>
        }
      />
      {online ? null : (
        <Notice tone="info">No connection to Wodi. Saving waits for the connection.</Notice>
      )}
      {maternity.isPending ? <LoadingBlock label="Loading maternity" rows={6} /> : null}
      {maternity.isError ? (
        <Notice
          tone="error"
          action={<Button onPress={() => void maternity.refetch()}>Try again</Button>}
        >
          {errorText(maternity.error, 'Maternity could not be loaded. Try again.')}
        </Notice>
      ) : null}
      {maternity.isSuccess ? <Loaded view={maternity.data} mayRecord={mayRecord} /> : null}
    </div>
  );
}

function Loaded({ view, mayRecord }: { view: MaternityView; mayRecord: boolean }) {
  const current = view.pregnancies.find((r) => r.pregnancy.status === 'open');
  const earlier = view.pregnancies.filter((r) => r !== current);
  return (
    <>
      {current === undefined ? (
        <Section title="Pregnancy" description="No open pregnancy.">
          {mayRecord ? <OpenPregnancyForm view={view} /> : null}
        </Section>
      ) : (
        <CurrentPregnancy view={view} record={current} mayRecord={mayRecord} />
      )}
      {earlier.length === 0 ? null : (
        <Section title="Earlier pregnancies">
          {earlier.map((r) => (
            <div key={r.pregnancy.id} className={styles.baby}>
              <p className={styles.legend}>
                {`${PREGNANCY_STATUS_LABELS[r.pregnancy.status]}${r.pregnancy.outcome === null ? '' : `: ${PREGNANCY_OUTCOME_LABELS[r.pregnancy.outcome]}`}`}
              </p>
              <PregnancyFacts p={r.pregnancy} />
              <LabourAndDelivery record={r} />
              {r.ancContacts.length === 0 ? null : <AncTable record={r} />}
            </div>
          ))}
        </Section>
      )}
    </>
  );
}
